import ApartmentOutlinedIcon from '@mui/icons-material/ApartmentOutlined';
import { Alert, Box, Button, Card, CardContent, Stack, Typography } from '@mui/material';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { CompanyLogo } from '@/components/common/CompanyLogo';
import { ROUTES } from '@/constants/routes';
import { useToast } from '@/hooks/useToast';
import { formatShortDate } from '@/utils/dateUtils';

import { ROLE_LABELS } from '../constants';
import { useInvitationMutations } from '../hooks/useInvitationMutations';
import type { MyCompanyInvitation } from '../types/companyPortal.types';
import { useWorkspace } from '../workspace/WorkspaceProvider';

interface PendingInvitationsListProps {
  invitations: MyCompanyInvitation[];
  /** Called after a successful accept, before navigation, so callers can dismiss surrounding UI. */
  onAccepted?: () => void;
}

/**
 * Renders the signed-in user's discovered company invitations (Invitation Discovery) with per-row
 * Accept / Decline. Accept redeems by id — the server binds the caller to the invitation by their
 * verified email — then activates the workspace and lands the user in it; Decline resolves the row.
 * Shared by the onboarding "Pending invitations" card and the /account/invitations page.
 */
export function PendingInvitationsList({ invitations, onAccepted }: PendingInvitationsListProps) {
  const { acceptInvitationById, rejectInvitationById } = useInvitationMutations();
  const workspace = useWorkspace();
  const toast = useToast();
  const navigate = useNavigate();
  // The invitation id currently being acted on, so only its buttons show the busy state.
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const onAccept = async (invitation: MyCompanyInvitation) => {
    setError(null);
    setBusyId(invitation.invitationId);
    try {
      const outcome = await acceptInvitationById(invitation.invitationId);
      if (!outcome.ok) {
        setError(
          outcome.errors[0]?.message ?? 'We could not accept the invitation. Please try again.',
        );
        return;
      }
      onAccepted?.();
      if (outcome.companyId) workspace.setActiveCompany(outcome.companyId);
      await workspace.refetch();
      toast.showSuccess(`Welcome to ${invitation.companyName}!`);
      navigate(ROUTES.company, { replace: true });
    } finally {
      setBusyId(null);
    }
  };

  const onDecline = async (invitation: MyCompanyInvitation) => {
    setError(null);
    setBusyId(invitation.invitationId);
    try {
      const outcome = await rejectInvitationById(invitation.invitationId);
      if (!outcome.ok) {
        setError(outcome.errors[0]?.message ?? 'We could not decline the invitation.');
        return;
      }
      toast.showSuccess(`Invitation from ${invitation.companyName} declined.`);
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Stack spacing={2}>
      {error ? (
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      ) : null}

      {invitations.map((invitation) => {
        const busy = busyId === invitation.invitationId;
        return (
          <Card key={invitation.invitationId} variant="outlined">
            <CardContent>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={2}
                alignItems={{ xs: 'stretch', sm: 'center' }}
              >
                <CompanyLogo
                  logoUrl={invitation.companyLogoUrl}
                  name={invitation.companyName}
                  size={48}
                  sx={{ bgcolor: 'primary.main' }}
                >
                  <ApartmentOutlinedIcon />
                </CompanyLogo>

                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                  <Typography variant="subtitle1" component="h3" noWrap>
                    {invitation.companyName}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Invited as <strong>{ROLE_LABELS[invitation.role]}</strong>
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Valid until {formatShortDate(invitation.expiresAt)}
                  </Typography>
                </Box>

                <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
                  <Button
                    variant="contained"
                    onClick={() => void onAccept(invitation)}
                    disabled={busy}
                    sx={{ minHeight: 44 }}
                  >
                    {busy ? 'Accepting…' : 'Accept'}
                  </Button>
                  <Button
                    variant="outlined"
                    color="inherit"
                    onClick={() => void onDecline(invitation)}
                    disabled={busy}
                    sx={{ minHeight: 44 }}
                  >
                    Decline
                  </Button>
                </Stack>
              </Stack>
            </CardContent>
          </Card>
        );
      })}
    </Stack>
  );
}
